import React, { useRef } from 'react';
import { Modal, Box, Typography, Button, Stack, IconButton, List, ListItem } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react'; // Use QRCodeCanvas for rendering the QR code
import { AppColors } from '../Utils/helper/AppColors';
import { CopyIcon, DownloadIcon, InfoIcon, IosLink, QrCodeScannerIcon } from './Icons/Icons';
import { useNavigate } from 'react-router-dom';
import { theme } from 'antd';
import InboundHeader from '../Screens/Inbound/InboundCall/InboundHeader';
import TextLogo from './TextLogo';
import { copyToClipboard, getColorSettings } from '../Utils/helper/commonUtils';
import IconHeader from './IconHeader';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import addie from '../Assets/Image/addie.svg';
import cadientlogo from '../Assets/Image/cadientlogo.svg';

const QrCodeModal = ({ open, onClose, companyId, company, scannerComponent }) => {
	const modalRef = useRef(); // Reference to the modal content
	const buttonRef = useRef();

	const navigate = useNavigate();

	const localSettingColors = getColorSettings();

	const handleDownloadPDF = async () => {
    try {
        // Hide the button before generating the PDF
        if (buttonRef.current) {
            buttonRef.current.style.display = 'none';
        }

        const element = modalRef.current; // Get the modal content
        const canvas = await html2canvas(element, { scale: 1.2, useCORS: true, logging: true }); // Capture the modal as a canvas
        const imgData = canvas.toDataURL('image/png'); // Convert canvas to image
        const pdf = new jsPDF('p', 'mm', 'a4'); // Create a new PDF instance
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width; // Maintain aspect ratio

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight); // Add image to PDF
        pdf.save('QrCodeModal.pdf'); // Download the PDF

        onClose(); // Close the modal after download
    } catch (error) {
        console.error('Error generating PDF:', error);
    }
};

	return (
		<Modal
			open={open}
			onClose={onClose}
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				overflow: 'auto',
				
			}}
		>
			<Box
				ref={modalRef} // Reference the modal content
				sx={{
					bgcolor: AppColors.WHITE,
					p: '0 50px 24px 50px',
					borderRadius: '30px',
					boxShadow: 24,
					width: '662px',
					textAlign: 'start',
					position: 'relative',
					overflow: 'auto',
					maxHeight: 'calc(100vh - 60px)', // Adjust for header height
				}}
			>
				{/* Header Section */}
				<IconHeader
					leftContent={{ src: addie, alt: 'Addie Logo' }}
					leftImgSize={40}
					// leftTextColor={localSettingColors?.fontColor}
					// middleContent={{ src: addie, alt: 'Addie Logo' }}
					// middleImgSize={40}
					// rightContent={<TextLogo img />}
					rightContent={{ src: cadientlogo, alt: 'Cadient Logo' }}
					rightImgSize={40}
					height={100}
					//   borderBottom={'2px solid '}
					//   borderColor={localSettingColors?.quinary}
				/>

				<Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
					{/* Title */}
					<Typography
						sx={{
							fontWeight: 600,
							fontStyle: 'SemiBold',
							fontSize: '24px',
							lineHeight: '130%',
							letterSpacing: '0%',
						}}
					>
						Scan a QR Code
					</Typography>

					{/* Scan QR Button */}
					<Button
						ref={buttonRef}
						variant="contained"
						color="info"
						sx={{ width: '169px' }}
						onClick={handleDownloadPDF}
						startIcon={<DownloadIcon size={20} color={AppColors.WHITE} />}
					>
						Download PDF
					</Button>
				</Stack>

				{/* Description */}
				<Typography
					sx={theme => ({
						fontWeight: 400,
						fontStyle: 'Regular',
						fontSize: '14px',
						lineHeight: '150%',
						letterSpacing: '0%',
						color: theme.palette.secondary.grey_text,
						mb: 1,
					})}
				>
					Use your device camera to scan a QR code. Detected links will open automatically.
				</Typography>

				{/* Steps */}
				<Typography
					sx={{
						fontWeight: 600,
						fontSize: '16px',
						lineHeight: '150%',
						letterSpacing: '0%',
						color: AppColors.HEADING_TITLE3,
					}}
				>
					Steps to Follow:
				</Typography>
				<List sx={{ listStyleType: 'disc', pl: 2 }}>
					<ListItem sx={theme => ({ display: 'list-item', fontSize: '14px', padding: '0 16px 4px 16px', color: theme.palette.secondary.grey_text })}>
						Scan the QR code using your phone’s camera.
					</ListItem>
					<ListItem sx={theme => ({ display: 'list-item', fontSize: '14px', padding: '0 16px 4px 16px', color: theme.palette.secondary.grey_text })}>
						Enter your corporate email and verify it using the OTP sent to your inbox.
					</ListItem>
					<ListItem sx={theme => ({ display: 'list-item', fontSize: '14px', padding: '0 16px 4px 16px', color: theme.palette.secondary.grey_text })}>
						Select the agent and choose the conversation type.
					</ListItem>
					<ListItem sx={theme => ({ display: 'list-item', fontSize: '14px', padding: '0 16px 4px 16px', color: theme.palette.secondary.grey_text })}>
						Submit your request – then sit back, you’ll receive a call within 2 minutes.
					</ListItem>
				</List>

				{/* Scanner Box */}
				<Box
					sx={(theme) => ({
						bgcolor: theme.palette.primary.lightest_shade,
						borderRadius: '20px',
						display: 'flex',
						flexDirection: 'column',
						height: '430px',
						mb: 4,
					})}
				>
					<Stack
						sx={(theme) => ({
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							justifyContent: 'space-between',
							py: 2,
							px: 2,
							bgcolor: theme.palette.primary.lighter_shade,
							borderRadius: '20px 20px 0 0',
						})}
					>
						<Stack direction={'row'} alignItems={'center'} gap={1}>
							<QrCodeScannerIcon size={18} />
							<Typography
								sx={{
									fontWeight: 400,
									fontStyle: 'Regular',
									fontSize: '14px',
									lineHeight: '121%',
									letterSpacing: '0%',
								}}
							>
								QR Scanner
							</Typography>
						</Stack>

						<Stack direction={'row'} alignItems={'center'} gap={1}>
							<InfoIcon size={18} />
							<Typography
								sx={{
									fontWeight: 400,
									fontStyle: 'Regular',
									fontSize: '14px',
									lineHeight: '121%',
									letterSpacing: '0%',
								}}
							>
								Allow camera access to start scanning
							</Typography>
						</Stack>
					</Stack>

					<Stack
						sx={(theme) => ({
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
						})}
					>
						<Stack
							sx={(theme) => ({
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								bgcolor: theme.palette.primary.lighter_shade,
								width: '250px',
								height: '250px',
								borderRadius: '15px',
								boxShadow: '2px 7px 7px 0px #5C5C5C40',
								position: 'relative',
								mb: 3,
							})}
						>
							{/* QR Code */}
							{/* <QRCodeCanvas value={`${window.location.origin}/inbound-call/${companyId}`} size={200} bgColor="transparent" />{' '} */}
							<QRCodeCanvas value={`${window.location.origin}/smart-feedback/inbound-call/${companyId}`} size={200} bgColor="transparent" />{' '}
							{/* Corners Layer */}
							<Box
								sx={{
									position: 'absolute',
									inset: 0,
									borderRadius: 2,
									pointerEvents: 'none',

									'& .corner': {
										position: 'absolute',

										width: '16px',
										height: '16px',
										border: '2px solid black',
									},
									'& .top-left': {
										top: 16,
										left: 16,
										borderRight: 'none',
										borderBottom: 'none',
									},
									'& .top-right': {
										top: 16,
										right: 16,
										borderLeft: 'none',
										borderBottom: 'none',
									},
									'& .bottom-left': {
										bottom: 16,
										left: 16,
										borderRight: 'none',
										borderTop: 'none',
									},
									'& .bottom-right': {
										bottom: 16,
										right: 16,
										borderLeft: 'none',
										borderTop: 'none',
									},
								}}
							>
								<span className="corner top-left"></span>
								<span className="corner top-right"></span>
								<span className="corner bottom-left"></span>
								<span className="corner bottom-right"></span>
							</Box>
						</Stack>
						<Stack direction={'row'} alignItems={'center'} >
							{/* <Typography
								sx={{
									fontWeight: 600,
									fontStyle: 'SemiBold',
									fontSize: '14px',
									lineHeight: '150%',
									letterSpacing: '0%',
									color: AppColors.HEADING_TITLE3,
								}}
							>
								Link :
							</Typography> */}

							<Stack
								sx={(theme) => ({
									backgroundColor: theme.palette.primary.lightest_shade,
									borderRadius: '10px',
									px: 1,
									py: 1,
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									gap: 1,
								})}
							>
								<IosLink size={10} />
								<Typography
									sx={(theme) => ({
										fontWeight: 400,
										fontStyle: 'Regular',
										fontSize: '9px',
										lineHeight: '121%',
										letterSpacing: '0%',
										// wordBreak: 'break-word',
										cursor: 'pointer',
										textWrap: 'nowrap',
										'&:hover': { color: theme.palette.primary.darker_primary },
									})}
									onClick={() => {
										// window.open(`/inbound-call/${companyId}`, '_blank');
										window.open(`/smart-feedback/inbound-call/${companyId}`, '_blank');
									}}
								>
									{/* {`${window.location.origin}/inbound-call/${companyId}`} */}
									{`${window.location.origin}/smart-feedback/inbound-call/${companyId}`}
								</Typography>

								<IconButton onClick={() =>
								    // copyToClipboard(`${window.location.origin}/inbound-call/${companyId}`
									copyToClipboard(`${window.location.origin}/smart-feedback/inbound-call/${companyId}`

									 )} size="small">
									<CopyIcon size={14} />
								</IconButton>
							</Stack>
						</Stack>
					</Stack>
				</Box>

				{/* Direct Link */}
				{/* <Stack direction={'row'} alignItems={'center'} gap={'12px'}>
					<Typography
						sx={{
							fontWeight: 600,
							fontStyle: 'SemiBold',
							fontSize: '14px',
							lineHeight: '150%',
							letterSpacing: '0%',
							color: AppColors.HEADING_TITLE3,
						}}
					>
						Link :
					</Typography>

					<Stack
						sx={(theme) => ({
							backgroundColor: theme.palette.primary.lightest_shade,
							borderRadius: '10px',
							px: 2,
							py: 1,
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: 1,
						})}
					>
						<IosLink size={10} />
						<Typography
							sx={(theme) => ({
								fontWeight: 400,
								fontStyle: 'Regular',
								fontSize: '10px',
								lineHeight: '121%',
								letterSpacing: '0%',
								// wordBreak: 'break-word',
								cursor: 'pointer',
								textWrap: 'nowrap',
								'&:hover': { color: theme.palette.primary.darker_primary },
							})}
							onClick={() => {
								window.open(`/inbound-call/${companyId}`, '_blank');
							}}
						>
							{`${window.location.origin}/inbound-call/${companyId}`}
						</Typography>

						<IconButton onClick={() => copyToClipboard(`${window.location.origin}/inbound-call/${companyId}`)} size="small">
							<CopyIcon size={14} />
						</IconButton>
					</Stack>
				</Stack> */}
			</Box>
		</Modal>
	);
};

export default QrCodeModal;
